import {
  Box,
  Text,
  VStack,
  Image,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdPlayArrow } from 'react-icons/md';
import type { ContentItem } from '../../types';

export interface VideoContentProps {
  content: ContentItem & { data?: any };
  onComplete?: (contentId: string) => void;
}

function extractVideoId(url: string): string | null {
  if (!url || !url.trim()) return null;
  const youtubeRegex =
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
  const youtubeMatch = url.match(youtubeRegex);
  const vimeoMatch = url.match(vimeoRegex);
  if (youtubeMatch) return youtubeMatch[1];
  if (vimeoMatch) return vimeoMatch[1];
  return null;
}

function getVideoType(url: string): 'youtube' | 'vimeo' | 'other' {
  if (!url || !url.trim()) return 'other';
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo.com')) return 'vimeo';
  return 'other';
}

function getEmbedUrl(url: string): string {
  const id = extractVideoId(url);
  const type = getVideoType(url);
  if (!id && type !== 'other') return '';
  switch (type) {
    case 'youtube':
      return `https://www.youtube.com/embed/${id}?autoplay=1`;
    case 'vimeo':
      return `https://player.vimeo.com/video/${id}?autoplay=1`;
    case 'other':
      return url;
  }
}

function getThumbnailUrl(url: string): string {
  const id = extractVideoId(url);
  const type = getVideoType(url);
  if (!id) return '';
  switch (type) {
    case 'youtube':
      return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
    case 'vimeo':
      return `https://via.placeholder.com/480x360?text=Vimeo+Thumbnail`;
    default:
      return '';
  }
}

export function VideoContent({ content }: VideoContentProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  const bgColor = useColorModeValue('gray.700', 'gray.700');
  const textColor = useColorModeValue('white', 'white');

  const videoUrl: string = content.data?.videoUrl;
  //const videoType = getVideoType(videoUrl);
  const embedUrl = getEmbedUrl(videoUrl);
  const thumbnailUrl = getThumbnailUrl(videoUrl);

  if (!videoUrl) {
    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color={textColor}>No video available</Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Box
          position="relative"
          width="100%"
          paddingBottom="56.25%"
          borderRadius="md"
          overflow="hidden"
          border="1px solid"
          borderColor="gray.200"
          bg="black"
        >
          {!isPlaying && thumbnailUrl && (
            <Box
              as="button"
              onClick={() => setIsPlaying(true)}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              zIndex={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              _hover={{ bg: 'rgba(0, 0, 0, 0.5)' }}
              transition="background 0.2s ease"
            >
              <Image
                src={thumbnailUrl}
                alt="Thumbnail"
                objectFit="cover"
                width="100%"
                height="100%"
              />
              <IconButton
                icon={<MdPlayArrow />}
                aria-label="Play video"
                size="lg"
                isRound
                colorScheme="whiteAlpha"
                fontSize="40px"
                bg="whiteAlpha.700"
                _hover={{ bg: 'whiteAlpha.900' }}
                position="absolute"
              />
            </Box>
          )}

          {isPlaying && embedUrl && (
            <iframe
              src={embedUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Embedded Video"
            //onLoad={handleComplete}
            />
          )}
        </Box>

      </Box>
    </VStack>
  );
}
